import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiOutlineDocumentReport, HiOutlineDocumentText, HiOutlineUserGroup, HiOutlineCog } from "react-icons/hi";
import { MdDashboard, MdShoppingCart, MdExpandLess } from "react-icons/md";
import { BiSolidNotepad, BiMoney } from "react-icons/bi";
import "../../index.css";

import { useTenant } from "../../hooks";

const MenuSide = ({ openMenu }) => {
  const [t] = useTranslation("global");
  const { tenantUsername } = useTenant();
  const location = useLocation();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [hoveredItemId, setHoveredItemId] = useState(null);
  const [hoverTooltip, setHoverTooltip] = useState(null);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const scrollContainerRef = useRef(null);
  const tooltipHideTimeoutRef = useRef(null);

  const activeBackgroundColor = "#699635";
  const hoverBackgroundColor = "#7aa93f";
  const menuItems = [
    {
      id: "dashboard",
      title: t("menu.Dashboard"),
      to: `/${tenantUsername}/dashboard`,
      active: location.pathname === `/${tenantUsername}/dashboard`,
      icon: MdDashboard,
      iconClassName: "w-5 h-5",
      end: true,
    },
    {
      id: "invoices",
      title: t("menu.Invoices"),
      to: `/${tenantUsername}/invoices`,
      active: location.pathname.includes("invoices"),
      icon: HiOutlineDocumentText,
      iconClassName: "w-6 h-6",
      end: true,
    },
    {
      id: "quotes",
      title: t("quotes.quotesList"),
      to: `/${tenantUsername}/quotes`,
      active: location.pathname.includes("quotes"),
      icon: BiSolidNotepad,
      iconClassName: "w-6 h-6",
      end: true,
    },
    {
      id: "products",
      title: t("menu.Products"),
      to: `/${tenantUsername}/products-home`,
      active: location.pathname.includes("products-home"),
      icon: MdShoppingCart,
      iconClassName: "w-6 h-6",
      end: true,
    },
    {
      id: "transactions",
      title: t("Transactions.Payments and Receipts"),
      to: `/${tenantUsername}/transactions`,
      active: location.pathname.includes("transactions"),
      icon: BiMoney,
      iconClassName: "w-6 h-6",
      end: true,
    },
    {
      id: "vendors",
      title: t("vendors.vendors"),
      to: `/${tenantUsername}/vendors`,
      active: location.pathname.includes("vendors"),
      icon: HiOutlineUserGroup,
      iconClassName: "w-6 h-6",
      end: true,
    },
    {
      id: "reports",
      title: t("reports.Report_Settings"),
      to: `/${tenantUsername}/reports`,
      active: location.pathname.includes("reports"),
      icon: HiOutlineDocumentReport,
      iconClassName: "w-6 h-6",
      end: true,
    },
    {
      id: "settings",
      title: t("setting.System_Settings"),
      to: `/${tenantUsername}/settings`,
      active: location.pathname.includes("settings"),
      icon: HiOutlineCog,
      iconClassName: "w-6 h-6",
      end: true,
    },
  ];

  useEffect(() => {
    if (openMenu) {
      setIsTooltipVisible(false);
      setHoverTooltip(null);
    }
  }, [openMenu]);

  useEffect(() => {
    return () => {
      if (tooltipHideTimeoutRef.current) {
        clearTimeout(tooltipHideTimeoutRef.current);
      }
    };
  }, []);

  const handleScroll = (event) => {
    const container = event.target;
    setShowScrollTop(container.scrollTop > 0);
  };

  const scrollToTop = () => {
    if (!scrollContainerRef.current) {
      return;
    }

    scrollContainerRef.current.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const setTooltipFromElement = (title, element, backgroundColor) => {
    if (!title || openMenu) {
      return;
    }

    if (tooltipHideTimeoutRef.current) {
      clearTimeout(tooltipHideTimeoutRef.current);
      tooltipHideTimeoutRef.current = null;
    }

    const rect = element.getBoundingClientRect();
    const dir = document?.documentElement?.dir || "rtl";
    const isRTL = dir === "rtl";

    setIsTooltipVisible(false);
    setHoverTooltip({
      title,
      top: rect.top,
      height: rect.height,
      backgroundColor,
      isRTL,
      left: rect.right,
      right: window.innerWidth - rect.left,
    });

    requestAnimationFrame(() => setIsTooltipVisible(true));
  };

  const onItemEnter = (item) => (event) => {
    setHoveredItemId(item.id);
    setTooltipFromElement(item.title, event.currentTarget, hoverBackgroundColor);
  };

  const onItemLeave = () => {
    setHoveredItemId(null);
    setIsTooltipVisible(false);
    tooltipHideTimeoutRef.current = setTimeout(() => {
      setHoverTooltip(null);
      tooltipHideTimeoutRef.current = null;
    }, 180);
  };

  return (
    <div
      className={`z-10 flex h-screen flex-col items-center justify-start transition-all duration-500 ease-out primary-bg-color ${
        openMenu
          ? "fixed w-10 flex md:relative md:w-40"
          : "fixed hidden w-0 md:flex md:w-[48px]"
      }`}
    >
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="flex w-full items-center justify-center py-2 transition-colors hover:bg-white/10"
          title={t("menu.scrollTop") || "Scroll to top"}
        >
          <MdExpandLess className="h-5 w-5 text-white" />
        </button>
      )}

      <ul
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="scrollbar-hide relative mt-4 flex w-full flex-1 flex-col items-center gap-3 overflow-y-scroll text-xs text-white"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isHovered = hoveredItemId === item.id;
          const isHighlighted = item.active || isHovered;
          const overlayBackgroundColor = isHovered
            ? hoverBackgroundColor
            : item.active
              ? activeBackgroundColor
              : hoverBackgroundColor;

          return (
            <li
              key={item.id}
              data-title={item.title}
              className={`menu-item w-full gap-2 flex items-center justify-center relative overflow-hidden menu-border-color py-3 transition-all duration-300 ${
                openMenu ? "justify-start" : "justify-center"
              }`}
              onMouseEnter={onItemEnter(item)}
              onMouseLeave={onItemLeave}
            >
              <span
                className="absolute right-0 top-0 z-0 h-full transition-all duration-500 ease-out"
                style={{
                  width: isHighlighted ? "100%" : "0%",
                  backgroundColor: overlayBackgroundColor,
                }}
              />
              <NavLink
                to={item.to}
                className={`relative z-10 flex items-center justify-center gap-2 w-full h-full ${
                  openMenu ? "justify-start" : "justify-center"
                }`}
                end={item.end}
              >
                <Icon
                  className={`${item.iconClassName} z-50 transition-colors flex-shrink-0 ${
                    isHighlighted ? "text-white" : ""
                  }`}
                  title={item.title}
                />
                {openMenu && (
                  <span className="hidden whitespace-nowrap md:flex items-center">
                    {item.title}
                  </span>
                )}
              </NavLink>
            </li>
          );
        })}
      </ul>

      {!openMenu && hoverTooltip?.title && (
        <div
          className={`fixed !z-[9999999999999999999] flex items-center whitespace-nowrap text-xs text-white transition-opacity duration-200 ease-out ${
            isTooltipVisible ? "opacity-100" : "opacity-0"
          }`}
          style={{
            top: hoverTooltip.top,
            height: hoverTooltip.height,
            background: hoverTooltip.backgroundColor || hoverBackgroundColor,
            padding: "0 10px",
            borderBottom: "1px solid white",
            pointerEvents: "none",
            ...(hoverTooltip.isRTL
              ? { right: hoverTooltip.right, borderRadius: "5px 0 0 5px" }
              : { left: hoverTooltip.left, borderRadius: "0 5px 5px 0" }),
          }}
        >
          {hoverTooltip.title}
        </div>
      )}
    </div>
  );
};

export default MenuSide;
